import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Container, Row, Col, Form, FormControl, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faTwitter, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { Map, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Banner from '../components/Banner';
import '../css/Contact.css';
import { Helmet } from 'react-helmet';
import SchemaOrg from './SchemaOrg';
import i18n from '../i18n';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('../img/icons/marker-icon-2x-red.png'),
    iconUrl: require('../img/icons/marker-icon-red.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

export default function Contact() {
  const {t} = useTranslation();
  const lng = i18n.language.split('-')[0];

  const position = [50.8446, 3.3173];

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(0);

  const handleSubmit = (event) => {
    event.preventDefault();

    fetch('https://' + (process.env.NODE_ENV === 'development' ? 'dev.' : '') + 'pimaxplus.com/api/?action=contact', {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type':'application/json'
      },
      body: JSON.stringify({
        name: name,
        email: email,
        subject: 'Ingevuld contactformulier door ' + name,
        message: message + '\n\n' + (phone ? '\nTelefoon: ' + phone : '') + (company ? '\nBedrijf: ' + company : '')
      })
    })
    .then(result => result.json())
    .then((data) => {
      if (data.success) {
        setSent(1);
      } else {
        setSent(-1);
      }
    })
  }

  return (
    <>
      <Helmet>
        <title>Contact | pimaxplus.com &ndash; IT consultancy</title>
        <meta name="description" content={t('Questions, remarks, proposals to work together feel free to send me.')} />
        <meta property="og:description" content={t('Questions, remarks, proposals to work together feel free to send me.')} />
        <meta property="og:title" content="Contact | pimaxplus.com &ndash; IT consultancy" />
        <meta property="og:url" content={'https://pimaxplus.com/' + lng + '/contact'} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://pimaxplus.com/favicons/android-chrome-512x512.png" />
        <meta property="og:image_alt" content="pimaxplus.com" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@pimaxplus" />
        <meta name="twitter:title" content="Contact | pimaxplus.com &ndash; IT consultancy" />
        <meta name="twitter:description" content={t('Questions, remarks, proposals to work together feel free to send me.')} />
        <meta name="twitter:image" content="https://pimaxplus.com/favicons/android-chrome-512x512.png" />
      </Helmet>
      <Banner title="Contact" />
      <Container fluid>
        <Row>
          <Map center={position} zoom={13} maxZoom={20} zoomControl={true} scrollWheelZoom={false} attributionControl={false} animate={true}>
            <TileLayer url='https://{s}.tile.osm.org/{z}/{x}/{y}.png'/>
            <Marker position={position}>
              <Popup>
                <Trans i18nKey="My hometown">
                  My hometown <strong>Harelbeke</strong>
                </Trans>
              </Popup>
            </Marker>
          </Map>
        </Row>
      </Container>
      <Container className="contact-form mb-5">
        <Row>
          <Col sm={12} lg={4} className="contact-form-sidebar">
            <h3>{t('Keep in touch')}</h3>
            <p className="font-weight-bold">pimaxplus.com</p>
            <p>
              {link('info@pimaxplus.com', 'mailto:info@pimaxplus.com', faEnvelope, 'E-mail', 'ctEmail')}<br />
              {link('pimaxplus', 'https://linkedin.com/company/pimaxplus', faLinkedin, 'LinkedIn', 'ctLinkedIn')}<br />
              {link('@pimaxplus', 'https://twitter.com/pimaxplus', faTwitter, 'Twitter', 'ctTwitter')}<br />
              {link('@pimaxplus', 'https://www.facebook.com/pimaxplus', faFacebook, 'Facebook', 'ctFacebook')}
            </p>
          </Col>
          <Col sm={12} lg={8} className="contact-form-main">
            <Row>
              <Col>
                <h3>{t('Send a message')}</h3>
                <p className={sent <= 0 ? 'd-block' : 'd-none'}>
                  {t('Questions, remarks, proposals to work together feel free to send me.')}
                </p>
                <p className={(sent === 1 ? 'd-block' : 'd-none') + ' text-success'}>
                  {t('Your message has been sent. Expect a reply soon.')}
                </p>
                <p className={(sent === -1 ? 'd-block' : 'd-none') + ' text-danger'}>
                  {t('Something went wrong. Please try again or use a different way to contact me.')}
                </p>
              </Col>
            </Row>
            <Form action="/contact" onSubmit={handleSubmit} className={sent <= 0 ? 'visible' : 'invisible'}>
              <Row>
                <Col sm={12} md={6}>
                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text id="name-addon">
                        <FontAwesomeIcon icon={faUser} aria-label={t('Name')} aria-labelledby="name-addon" />
                      </InputGroup.Text>
                    </InputGroup.Prepend>
                    <FormControl name="name" placeholder={t('Name') + ' *'} value={name} aria-label={t('Name')} aria-describedby="name-addon" required={true} onChange={e => setName(e.target.value)} />
                  </InputGroup>
                </Col>
                <Col sm={12} md={6}>
                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text id="email-addon">
                        <FontAwesomeIcon icon={faEnvelope} aria-label={t('E-mail address')} aria-labelledby="email-addon" />
                      </InputGroup.Text>
                    </InputGroup.Prepend>
                    <FormControl type="email" name="email" placeholder={t('E-mail address') + ' *'} value={email} aria-label={t('E-mail address')} aria-describedby="email-addon" required={true} onChange={e => setEmail(e.target.value)} />
                  </InputGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={12} md={6}>
                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text id="phone-addon">
                        <FontAwesomeIcon icon={faPhone} aria-label={t('Phone number')} aria-labelledby="phone-addon" />
                      </InputGroup.Text>
                    </InputGroup.Prepend>
                    <FormControl name="phone" placeholder={t('Phone number')} value={phone} aria-label={t('Phone number')} aria-describedby="phone-addon" onChange={e => setPhone(e.target.value)} />
                  </InputGroup>
                </Col>
                <Col sm={12} md={6}>
                  <InputGroup className="mb-3">
                    <InputGroup.Prepend>
                      <InputGroup.Text id="company-addon">
                        <FontAwesomeIcon icon={faBuilding} aria-label={t('Company')} aria-labelledby="company-addon" />
                      </InputGroup.Text>
                    </InputGroup.Prepend>
                    <FormControl name="company" placeholder={t('Company')} value={company} aria-label={t('Company')} aria-describedby="company-addon" onChange={e => setCompany(e.target.value)} />
                  </InputGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormControl as="textarea" name="message" placeholder={t('Your message')} value={message} aria-label={t('Your message') } rows={10} required={true} onChange={e => setMessage(e.target.value)} />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col>
                  <Button type="submit" variant="primary">{t('Send')}</Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Container>
      <SchemaOrg 
        type="ContactPage"
        url={'https://pimaxplus.com/' + lng + '/contact'}
        datePublished="2020-07-12"
        description={t('Questions, remarks, proposals to work together feel free to send me.')} />
    </>
  )
}

function link(title, url, icon, label, labelledby) {
  return (
    <a href={url} title={title} target="_blank" rel="noopener noreferrer">
      <FontAwesomeIcon icon={icon} className="mr-2" aria-label={label} aria-labelledby={labelledby} />
      <span id={labelledby}>{title}</span>
    </a>
  );
}